import React, {useState} from 'react';
import {Stage} from '../Stage';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Typography from '@mui/material/Typography';
import classNames from './UploadStage.module.css'
import {UploadStage as UploadStageType} from "../../../api/application/getApplicationDetails";
import {getIsCompleted} from "./util";
import {Button} from "@mui/material";
import {useLazyQuery, useMutation} from "@apollo/client";
import {GET_PRESIGNED_URL} from "../../../api/stage/getPresignedUrl";
import Stack from "@mui/material/Stack";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import LoadingButton from "@mui/lab/LoadingButton";
import {useTheme} from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {CHANGE_APPLICATION_STAGE_STATUS} from "../../../api/application/changeApplicationStageStatus";

type UploadStageProps = {
    nextStageTitle?: string,
    refetch: () => void,
    stage: UploadStageType,
}

export const UploadStage = (props: UploadStageProps) => {
    const {nextStageTitle, refetch, stage} = props

    const [isModalOpen, setIsModalOpen] = useState(false)

    const [changeStatus, {loading}] = useMutation(CHANGE_APPLICATION_STAGE_STATUS)

    const [getTheFileUrl] = useLazyQuery(GET_PRESIGNED_URL);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const isCompleted = getIsCompleted(stage)

    const onChangeStatus = async () => {
        const {errors} = await changeStatus({variables: {applicationStageId: stage.id, newStatus: 'APPROVED'}})

        if (errors === undefined || errors.length === 0) {
            setIsModalOpen(false)

            await refetch()
        }
    }

    const handleDownload = async (fileId: string) => {
        const {error, data} = await getTheFileUrl({variables: {fileId}})

        if (!error && data !== undefined) {
            const link = document.createElement('a');
            link.download = 'Example-PDF-File';
            link.href = data.getPresignedUrl;
            link.click();
        }
    };

    return (
        <>
            <Stage
                icon={isCompleted ? <CheckCircleOutlineIcon color="success" /> : <AccessTimeIcon color="warning"/>}
                id={stage.id}
                subTitle={isCompleted ? `Completed ${new Date(stage.updatedAt).toDateString()}` : 'Not yet completed'}
                title={stage.title}
            >
                <ul>
                    {stage.files.map(file => (
                        <li key={file.title}>
                            <div className={classNames.listItem}>
                                <Typography>
                                    {`File: ${file.title}`}
                                </Typography>

                                <Typography>
                                    {file.fileId ? (
                                        <Button onClick={() => handleDownload(file.fileId)}>
                                            Download File
                                        </Button>
                                    ) : (
                                        <Typography color="gray">File Not Submitted</Typography>
                                    )}
                                </Typography>
                            </div>
                        </li>
                    ))}
                </ul>

                <Stack direction="row" gap="1rem" justifyContent="flex-end" marginTop="1rem">
                    <Button
                        disabled={isCompleted}
                        color="success"
                        variant="contained"
                        onClick={() => setIsModalOpen(true)}
                    >
                        Proceed to Next Stage
                    </Button>
                </Stack>

                <Dialog
                    fullScreen={fullScreen}
                    open={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                >
                    <DialogTitle>
                        Progress Application
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {nextStageTitle
                                ? (<span>The next stage is <strong>{nextStageTitle}.</strong> </span>)
                                : (<span>This is the last stage. </span>)
                            }
                            Are you sure you'd like to progress this application to the next stage?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            color="error"
                            onClick={() => setIsModalOpen(false)}
                            variant="contained"
                            disabled={loading}
                        >
                            No, Go Back
                        </Button>
                        <LoadingButton
                            color="success"
                            onClick={() => onChangeStatus()}
                            variant="contained"
                            disabled={loading}
                            loading={loading}
                        >
                            Yes, Proceed
                        </LoadingButton>
                    </DialogActions>
                </Dialog>
            </Stage>
        </>
    )
}
